import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, ShieldCheck, ArrowRight } from "lucide-react";
import { SITE } from "@/data/site";
import { getAreas } from "@/data/areas";
import { useLang } from "@/lib/i18n";

export default function MapSideCTA() {
  const { t, lang } = useLang();
  const AREAS = getAreas(lang);

  return (
    <div
      data-testid="map-side-cta"
      className="relative h-full bg-gradient-to-br from-brand-navy via-brand-navy-dark to-slate-900 rounded-3xl border border-slate-800 overflow-hidden p-8 sm:p-10 flex flex-col"
    >
      <div className="absolute -top-24 -right-20 w-72 h-72 rounded-full bg-brand-yellow/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-20 w-72 h-72 rounded-full bg-brand-navy-light/25 blur-3xl pointer-events-none" />

      <div className="relative flex-1 flex flex-col">
        <div className="inline-flex items-center gap-2 self-start rounded-full bg-white/10 backdrop-blur px-3 py-1.5 text-xs font-bold text-brand-yellow uppercase tracking-wider">
          <MapPin size={13} />
          {t.mapSide.eyebrow}
        </div>

        <h3 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-[2.6rem] font-bold text-white leading-[1.1] text-balance">
          {t.mapSide.title}
        </h3>

        <p className="mt-4 text-white/80 text-base sm:text-lg leading-relaxed">
          {t.mapSide.sub}
        </p>

        <div className="mt-6 grid grid-cols-2 gap-2.5">
          {AREAS.map((a) => (
            <Link
              key={a.slug}
              to={`/service-areas/${a.slug}`}
              data-testid={`map-side-area-${a.slug}`}
              className="group flex items-center justify-between gap-2 bg-white/5 hover:bg-brand-yellow text-white hover:text-slate-900 border border-white/15 hover:border-brand-yellow rounded-xl px-3.5 py-2.5 transition-colors"
            >
              <span className="text-sm font-semibold leading-tight">
                {a.name.replace(/ County$| Condado de /, "").replace(/Condado de /, "")}
              </span>
              <ArrowRight size={14} className="opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </Link>
          ))}
        </div>

        <div className="mt-7 grid grid-cols-3 gap-3 text-white">
          <Mini icon={Clock} label="24/7" sub={t.mapSide.dispatch} />
          <Mini icon={ShieldCheck} label={lang === "es" ? "Licenciados" : "Licensed"} sub={t.mapSide.andInsured} />
          <Mini icon={MapPin} label="4" sub={t.mapSide.counties} />
        </div>

        <div className="mt-auto pt-7 flex flex-col sm:flex-row gap-3">
          <a
            data-testid="map-side-call"
            href={`tel:${SITE.primaryPhone}`}
            className="inline-flex items-center justify-center gap-2 bg-brand-yellow hover:bg-brand-yellow-dark text-slate-900 font-bold py-3.5 px-5 rounded-xl transition-colors whitespace-nowrap shadow-lg shadow-yellow-200/20 flex-1"
          >
            <Phone size={17} />
            <span>{SITE.primaryPhone}</span>
          </a>
          <Link
            data-testid="map-side-quote"
            to="/contact"
            className="inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-brand-yellow text-white hover:text-brand-yellow font-bold py-3.5 px-5 rounded-xl transition-colors whitespace-nowrap"
          >
            {t.cta.freeEstimate}
          </Link>
        </div>
      </div>
    </div>
  );
}

function Mini({ icon: Icon, label, sub }) {
  return (
    <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl px-3 py-2.5">
      <Icon size={16} className="text-brand-yellow" />
      <p className="font-heading text-lg font-bold leading-none mt-1.5">{label}</p>
      <p className="text-[11px] text-white/60 mt-0.5">{sub}</p>
    </div>
  );
}
